/**
 * Task 158 — pure date helpers for the "last 48h is provisional" UX on the
 * paid-media analytics panel.
 *
 * Meta keeps reshaping the most recent ~48h of metrics (the IC-5.3 sync
 * re-pulls a 3-day trailing window and overwrites it), so the latest two
 * calendar days (today + yesterday, UTC) are surfaced as PROVISIONAL.
 *
 * All functions are pure and take `now` explicitly, so they are deterministic
 * and unit-testable. Dates are compared as `YYYY-MM-DD` strings — lexicographic
 * order equals chronological order for that fixed-width format.
 */

/**
 * `YYYY-MM-DD` of `now − 1 day` (UTC). A daily metric date `>=` this value
 * falls within the last 48h (i.e. it is today or yesterday).
 */
export function provisionalFromDate(now: Date): string {
  return new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/**
 * True when the selected range's END touches the last 48h (today / yesterday).
 * Since `end >= start`, the range overlaps the last 48h iff `end` does.
 */
export function rangeIncludesLast48h(endDate: string, now: Date): boolean {
  if (!endDate) return false;
  return endDate >= provisionalFromDate(now);
}

/** True when a daily `metric_date` falls within the last 48h (today/yesterday). */
export function isProvisionalDate(metricDate: string, now: Date): boolean {
  if (!metricDate) return false;
  return metricDate >= provisionalFromDate(now);
}

/**
 * Index of the FIRST date in a date-ascending list that is on/after the
 * provisional floor (the chart's `provisionalFromDate`), or `-1` if there is
 * no floor or no provisional date. Anchors the chart's provisional band on the
 * DATE axis — independent of whether that day has a plotted (non-null) metric
 * value, so a null CTR/ROAS day inside the last 48h is still shaded (Codex P2).
 */
export function firstProvisionalIndex(
  dates: ReadonlyArray<string>,
  provisionalFromDate: string | undefined,
): number {
  if (!provisionalFromDate) return -1;
  return dates.findIndex((d) => d !== '' && d >= provisionalFromDate);
}
